import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Link, Redirect } from "react-router-dom";
import axios from "axios";
import {
  FaUserGraduate,
  FaSyncAlt,
  FaUserPlus,
  FaUsers,
  FaArrowUp,
  FaBus,
  FaExchangeAlt,
  FaQuestionCircle,
  FaMoneyBillWave,
  FaLink,
  FaFileAlt,
  FaHashtag,
  FaIdCard,
  FaCalendarAlt,
  FaTint,
  FaCreditCard,
  FaVenusMars,
  FaLanguage,
  FaRulerCombined,
  FaGraduationCap,
  FaHandHoldingHeart,
  FaWheelchair,
  FaPen,
  FaIdBadge,
  FaUserFriends,
  FaCalendarCheck,
  FaTag,
  FaList,
  FaUsersCog,
  FaPhoneAlt,
  FaInfoCircle,
  FaCogs,
  FaRandom,
  FaImage,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import "../css/Admin_css/admission-dashboard.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const INFO_CARDS = [
  { to: "/AdminMain/RollNumberManagement", title: "Roll Number", hint: "Assign & update rolls", icon: FaHashtag, tone: "roll" },
  { to: "/AdminMain/StateSpecificId", title: "State Specific ID", hint: "State ID details", icon: FaIdCard, tone: "id" },
  { to: "/AdminMain/Dob", title: "Date of Birth", hint: "Update DOB records", icon: FaCalendarAlt, tone: "dob" },
  { to: "/AdminMain/BloodGroup", title: "Blood Group", hint: "Blood group info", icon: FaTint, tone: "blood" },
  { to: "/AdminMain/Aadhaar", title: "Aadhar Number", hint: "Student Aadhaar", icon: FaCreditCard, tone: "aadhaar" },
  { to: "/AdminMain/Gender", title: "Gender", hint: "Gender details", icon: FaVenusMars, tone: "gender" },
  { to: "/AdminMain/MotherTongue", title: "Mother Tongue", hint: "Language details", icon: FaLanguage, tone: "tongue" },
  { to: "/AdminMain/HeightAndWeight", title: "Height And Weight", hint: "Physical metrics", icon: FaRulerCombined, tone: "hw" },
  { to: "/AdminMain/BplBeneficiary", title: "BPL Beneficiary", hint: "BPL status", icon: FaHandHoldingHeart, tone: "bpl" },
  { to: "/AdminMain/PhysicalDisability", title: "Physical Disability", hint: "Disability records", icon: FaWheelchair, tone: "pwd" },
  { to: "/AdminMain/StudentPen", title: "Student PEN", hint: "PEN numbers", icon: FaPen, tone: "pen" },
  { to: "/AdminMain/ManageStudentApaar", title: "Student APAAR", hint: "APAAR IDs", icon: FaIdBadge, tone: "apaar" },
  { to: "/AdminMain/ParentsAadhar", title: "Parents Aadhar", hint: "Parent Aadhaar", icon: FaUserFriends, tone: "parents" },
  { to: "/AdminMain/ManageAdmissionDate", title: "Admission Date", hint: "In-class admission date", icon: FaCalendarCheck, tone: "admdate" },
  { to: "/AdminMain/ManageAdmissionType", title: "Admission Type", hint: "Admission type tags", icon: FaTag, tone: "admtype" },
  { to: "/AdminMain/ManageCategory", title: "Category", hint: "Student category", icon: FaList, tone: "category" },
  { to: "/AdminMain/ManageCaste", title: "Caste", hint: "Caste details", icon: FaUsersCog, tone: "caste" },
  { to: "/AdminMain/ManageStudentContacts", title: "Student Contacts", hint: "Contact numbers", icon: FaPhoneAlt, tone: "contacts" },
  { to: "/AdminMain/ManageStudentImage", title: "Manage Student Image", hint: "Rotate, crop & reupload photos", icon: FaImage, tone: "photo" },
];

const ACTION_CARDS = [
  { to: "/AdminMain/StudentRegistration", title: "Student Registration", hint: "Register new students", icon: FaUserPlus, tone: "register" },
  { to: "/AdminMain/Siblings", title: "Siblings", hint: "Link sibling students", icon: FaUsers, tone: "siblings" },
  { to: "/AdminMain/PromoteStudent", title: "Promote Student", hint: "Into next session", icon: FaArrowUp, tone: "promote" },
  { to: "/AdminMain/PromoteTransportStudent", title: "Promote Transport Student", hint: "Into next session", icon: FaBus, tone: "transport" },
  { to: "/AdminMain/ClassChange", title: "Class Change", hint: "Current session", icon: FaExchangeAlt, tone: "class" },
  { to: "/AdminMain/SessionJump", title: "Session Jump", hint: "Register student into other sessions", icon: FaRandom, tone: "sessionjump" },
  { to: "/AdminMain/AdmissionQuery", title: "Admission Query", hint: "Enquiry follow-ups", icon: FaQuestionCircle, tone: "query" },
  { to: "/AdminMain/RegistrationFee", title: "Registration Fee", hint: "Fee collection", icon: FaMoneyBillWave, tone: "fee" },
  { to: "/AdminMain/RteStudent", title: "RTE Student", hint: "RTE list & fee details", icon: FaGraduationCap, tone: "rte" },
  { to: "/AdminMain/AdmissionLink", title: "Online Admission Link", hint: "Share admission form", icon: FaLink, tone: "link" },
  { to: "/AdminMain/TransferCertificate", title: "Manage TC", hint: "Transfer certificates", icon: FaFileAlt, tone: "tc" },
];

function formatCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return Math.round(n).toLocaleString("en-IN");
}

function resolveSession() {
  const session = SessionManager.getCurrentSession();
  if (Array.isArray(session) && session[0]) {
    return {
      id: String(session[0].session_id || ""),
      name: String(session[0].session_name || ""),
    };
  }
  if (session && typeof session === "object") {
    return {
      id: String(session.session_id || ""),
      name: String(session.session_name || ""),
    };
  }
  return { id: "", name: "" };
}

const HubTile = memo(function HubTile({ to, title, hint, icon: Icon, tone, index = 0 }) {
  return (
    <Link
      to={to}
      className={`ad-hub-tile ad-hub-tile--${tone}`}
      aria-label={title}
      style={{ "--ad-hub-i": index }}
      title={hint}
    >
      <span className={`ad-hub-tile__circle ad-hub-tile__circle--${tone}`} aria-hidden>
        {Icon ? <Icon /> : null}
      </span>
      <span className="ad-hub-tile__content">
        <span className="ad-hub-tile__title">{title}</span>
        <span className="ad-hub-tile__hint">{hint}</span>
      </span>
    </Link>
  );
});

const ActionTile = memo(function ActionTile({ to, title, hint, icon: Icon, tone, index = 0 }) {
  return (
    <Link
      to={to}
      className={`ad-action-tile ad-action-tile--${tone}`}
      aria-label={title}
      style={{ "--ad-hub-i": index }}
      title={hint}
    >
      <span className={`ad-action-tile__icon ad-action-tile__icon--${tone}`} aria-hidden>
        {Icon ? <Icon /> : null}
      </span>
      <span className="ad-action-tile__content">
        <span className="ad-action-tile__title">{title}</span>
        {hint ? <span className="ad-action-tile__hint">{hint}</span> : null}
      </span>
    </Link>
  );
});

const AdmissionDashboard = () => {
  const loggedIn = localStorage.getItem("token") != null;
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [totalStudents, setTotalStudents] = useState(0);
  const session = useMemo(() => resolveSession(), []);

  const loadDashboard = useCallback(
    async ({ soft = false } = {}) => {
      const schoolCode = SessionManager.getSchoolCode();
      const sessionId = resolveSession().id;

      if (!schoolCode) {
        myToast.notify_warning("School code is missing.");
        setLoading(false);
        setRefreshing(false);
        return;
      }
      if (!sessionId) {
        myToast.notify_warning("Session is missing.");
        setLoading(false);
        setRefreshing(false);
        return;
      }

      if (soft) setRefreshing(true);
      else setLoading(true);

      try {
        const res = await axios.get(`${API_BASE()}getAdmissionDashboard.php`, {
          params: {
            school_code: schoolCode,
            session_id: sessionId,
          },
        });
        const payload = res?.data;
        if (payload?.api_status === "OK" && Array.isArray(payload.data) && payload.data[0]) {
          setTotalStudents(Number(payload.data[0].total_student) || 0);
        } else {
          setTotalStudents(0);
          if (payload?.api_remark && !/no record/i.test(String(payload.api_remark))) {
            myToast.notify_warning(payload.api_remark);
          }
        }
      } catch (error) {
        console.error("[AdmissionDashboard] load error", error);
        setTotalStudents(0);
        myToast.notify_warning("Failed to load admission dashboard.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    []
  );

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  if (!loggedIn) return <Redirect to="/Login" />;

  return (
    <div
      className="content payroll-page ad-page"
      style={{
        background: COLORS.white,
        fontFamily:
          'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> Admission Dashboard</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp ad-shell">
        <header className="ad-hero" style={{ "--ad-hero-bg": HERO_GRADIENT }}>
          <div className="ad-hero__brand">
            <div className="ad-hero__icon" aria-hidden>
              <FaUserGraduate />
            </div>
            <div className="min-w-0">
              <h1 className="ad-hero__title">Admission Dashboard</h1>
              <p className="ad-hero__sub">
                {session.name ? `${session.name} · ` : ""}
                {formatCount(totalStudents)} student
                {totalStudents === 1 ? "" : "s"}
              </p>
            </div>
          </div>
          <div className="ad-hero__tools">
            <Link
              to="/AdminMain/StudentRegistration"
              className="ad-hero-btn"
              title="Student Registration"
            >
              <FaUserPlus aria-hidden />
              <span>Student Registration</span>
            </Link>
            <button
              type="button"
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              onClick={() => loadDashboard({ soft: true })}
              disabled={loading || refreshing}
              aria-label="Refresh admission data"
              title="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
          </div>
        </header>

        {loading ? (
          <div className="ad-loading">
            <MyLoader />
          </div>
        ) : (
          <>
            <section className="ad-section" aria-label="Admission stats">
              <div className="ad-stat">
                <div className="ad-stat__icon" aria-hidden>
                  <FaUsers />
                </div>
                <div className="ad-stat__body">
                  <div className="ad-stat__label">Total students</div>
                  <div className="ad-stat__value">{formatCount(totalStudents)}</div>
                  <div className="ad-stat__sub">Active this session</div>
                </div>
              </div>
            </section>

            <section className="ad-hub" aria-labelledby="ad-info-heading">
              <div className="ad-hub-panel">
                <div className="ad-hub__head">
                  <h2 id="ad-info-heading" className="ad-section-title">
                    <FaInfoCircle aria-hidden /> Manage student information
                  </h2>
                  <p className="ad-hub__sub">
                    Update profile fields and identity details
                  </p>
                </div>
                <div className="ad-hub-grid">
                  {INFO_CARDS.map((card, index) => (
                    <HubTile key={card.to} {...card} index={index} />
                  ))}
                </div>
              </div>
            </section>

            <section className="ad-hub ad-hub--last" aria-labelledby="ad-action-heading">
              <div className="ad-hub-panel">
                <div className="ad-hub__head">
                  <h2 id="ad-action-heading" className="ad-section-title">
                    <FaCogs aria-hidden /> Sub Modules
                  </h2>
                </div>
                <div className="ad-action-grid">
                  {ACTION_CARDS.map((card, index) => (
                    <ActionTile key={card.to} {...card} index={index} />
                  ))}
                </div>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default AdmissionDashboard;
